import { useCallback, useMemo } from 'react';
import { Selection } from '@ds';
import { OMSShiftOptionType } from '@lp/features/Scheduler/SchedulerOMS/types';
import {
  getShiftOptionFromShiftSlot,
  getShiftSlotFromShiftOption,
  isAdminDayOption,
  isOnCallOption,
  resolveDualSlotSelection,
} from '@lp/features/Scheduler/SchedulerOMS/utils';
import { omsShiftOptionDropdownItems } from '@lp/features/Scheduler/SchedulerOMS';
import { OMSShift, OMSShiftSelectPayload, OMSShiftSlotPayload } from '@/api/apis/resources/schedule';

const getSlotPayload = (option?: OMSShiftOptionType | null): OMSShiftSlotPayload | null | undefined => {
  if (!option) return option;
  return getShiftSlotFromShiftOption(option);
};

type UseOMSShiftCellDropdownParams = {
  shiftData?: OMSShift;
  onShiftChange: (shiftPayload?: OMSShiftSelectPayload) => void;
  onOpenChange: (isOpen: boolean) => void;
  canReset?: boolean;
  getResettableSlot?: (slotIndex: number) => boolean;
};

export const useOMSShiftCellDropdown = ({
  shiftData,
  onShiftChange,
  onOpenChange,
  canReset,
  getResettableSlot = () => true,
}: UseOMSShiftCellDropdownParams) => {
  const selectedSlot1Option = getShiftOptionFromShiftSlot(shiftData?.slot1);
  const selectedSlot2Option = getShiftOptionFromShiftSlot(shiftData?.slot2);
  const isLastSlot1OnCall = !getResettableSlot?.(1);
  const isLastSlot2OnCall = !getResettableSlot?.(2);
  const hasLastOnCallSlot = isLastSlot1OnCall || isLastSlot2OnCall;

  const disabledShiftKeys = useMemo(() => {
    if (!hasLastOnCallSlot) return [];
    return omsShiftOptionDropdownItems.filter((o) => !isOnCallOption(o));
  }, [hasLastOnCallSlot]);

  const selectedShiftKeys = useMemo(() => {
    const keys = [];
    if (selectedSlot1Option) keys.push(selectedSlot1Option);
    if (selectedSlot2Option) keys.push(selectedSlot2Option);
    return Array.from(new Set(keys));
  }, [selectedSlot1Option, selectedSlot2Option]);

  const getOptionSelectedSlotKeys = useCallback(
    (shiftOption: OMSShiftOptionType): string[] => {
      const keys: string[] = [];
      if (selectedSlot1Option === shiftOption) keys.push('1');
      if (selectedSlot2Option === shiftOption) keys.push('2');
      return keys;
    },
    [selectedSlot1Option, selectedSlot2Option],
  );

  // For single-slot shifts
  const handleSingleSlotSelection = useCallback(
    (selectedOption?: OMSShiftOptionType) => {
      if (!selectedOption) {
        if (canReset) {
          onShiftChange(undefined);
        }
      } else {
        // Set new single-slot shift: slot1 = payload, slot2 = null
        onShiftChange({
          slotsPerDay: 1,
          slot1: getSlotPayload(selectedOption),
          slot2: null,
        });
      }
      onOpenChange(false);
    },
    [canReset, onShiftChange, onOpenChange],
  );

  const onSelectionChange = useCallback(
    (keys: Selection) => {
      if (keys !== 'all') {
        const selected = Array.from(keys)[0] as OMSShiftOptionType | undefined;
        handleSingleSlotSelection(selected);
      }
    },
    [handleSingleSlotSelection],
  );

  // For dual-slot shifts
  const createSlotsSelectionHandler = useCallback(
    (optionType: OMSShiftOptionType) => (keys: Selection) => {
      if (keys === 'all') return;

      const selectedSlots = Array.from(keys).map((k) => Number(k));
      const desiredSlots = {
        slot1: selectedSlots.includes(1),
        slot2: selectedSlots.includes(2),
      };

      const { slot1, slot2 } = resolveDualSlotSelection(
        { slot1: selectedSlot1Option, slot2: selectedSlot2Option },
        optionType,
        desiredSlots,
      );

      if (!slot1 && !slot2) {
        if (canReset) {
          onShiftChange(undefined);
          onOpenChange(false);
        }
        return;
      }

      onShiftChange({
        slotsPerDay: 2,
        slot1: getSlotPayload(slot1),
        slot2: getSlotPayload(slot2),
      });
      onOpenChange(false);
    },
    [selectedSlot1Option, selectedSlot2Option, canReset, onShiftChange, onOpenChange],
  );

  // Determine disabled slots for dual-slot options
  const getDisabledSlotKeys = useCallback(
    (optionType: OMSShiftOptionType): string[] => {
      const keys: string[] = [];

      // Block On‑Call in Slot 1 when Admin Day occupies Slot 2 (forbidden combination).
      if (isOnCallOption(optionType) && isAdminDayOption(selectedSlot2Option)) keys.push('1');

      // Prevent removing the last On‑Call slot of the day when resetting is not allowed.
      if (!canReset) {
        if (selectedSlot1Option === optionType && (!selectedSlot2Option || isLastSlot1OnCall)) keys.push('1');
        if (selectedSlot2Option === optionType && (!selectedSlot1Option || isLastSlot2OnCall)) keys.push('2');
      }

      return Array.from(new Set(keys));
    },
    [canReset, isLastSlot1OnCall, isLastSlot2OnCall, selectedSlot1Option, selectedSlot2Option],
  );

  // Determine if empty selection should be disallowed for a specific option's dropdown
  // Only disallow when all selected slots are in this dropdown AND canReset is false
  const shouldDisallowEmptySelection = useCallback(
    (optionType: OMSShiftOptionType): boolean => {
      if (canReset) return false;

      const slot1Other = !!selectedSlot1Option && selectedSlot1Option !== optionType;
      const slot2Other = !!selectedSlot2Option && selectedSlot2Option !== optionType;

      return !slot1Other && !slot2Other;
    },
    [canReset, selectedSlot1Option, selectedSlot2Option],
  );

  return {
    selectedShiftKeys,
    disabledShiftKeys,
    getOptionSelectedSlotKeys,
    onSelectionChange,
    createSlotsSelectionHandler,
    getDisabledSlotKeys,
    shouldDisallowEmptySelection,
  };
};
